/**
 * Formatting Utilities
 *
 * Pure functions for date, time, and number formatting.
 */

/**
 * Format a date string for display.
 *
 * @param {string} dateString - ISO date string
 * @param {object} [options]
 * @param {boolean} [options.includeTime=false] - Include hours and minutes
 * @param {string} [options.emptyValue=""] - Return value when dateString is falsy
 * @returns {string}
 */
export function formatDate(dateString, options = {}) {
  const { includeTime = false, emptyValue = "" } = options;

  if (!dateString) return emptyValue;

  try {
    const date = new Date(dateString);
    const formatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };

    if (includeTime) {
      formatOptions.hour = "2-digit";
      formatOptions.minute = "2-digit";
    }

    return date.toLocaleDateString("fr-FR", formatOptions);
  } catch {
    return dateString;
  }
}

/**
 * Format a date as month + year only (e.g. "juillet 2026").
 *
 * @param {string} dateString - ISO date string
 * @param {object} [options]
 * @param {string} [options.emptyValue=""] - Return value when dateString is falsy
 * @returns {string}
 */
export function formatMonthYear(dateString, options = {}) {
  const { emptyValue = "" } = options;

  if (!dateString) return emptyValue;

  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("fr-FR", {
      month: "long",
      year: "numeric",
    }).format(date);
  } catch {
    return dateString;
  }
}

/**
 * Format a date as short (day/month only).
 *
 * @param {string} dateString - ISO date string
 * @param {object} [options]
 * @param {string} [options.emptyValue="N/A"] - Return value when dateString is falsy
 * @returns {string}
 */
export function formatShortDate(dateString, options = {}) {
  const { emptyValue = "N/A" } = options;

  if (!dateString) return emptyValue;

  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
    }).format(date);
  } catch {
    return dateString;
  }
}

/**
 * Format a future-or-past date as relative time (e.g., "demain", "dans 5 j",
 * "en retard de 3 j", "aujourd'hui"). Pivot opposé de `formatTimeAgo` :
 * lecture côté calendrier d'obligations (§15.D / EPIC-030).
 *
 * @param {string} dateString - ISO date string (DateTimeInterface::ATOM)
 * @returns {string}
 */
export function formatRelativeDate(dateString) {
  if (!dateString) return "";

  try {
    const diffMs = new Date(dateString) - Date.now();
    const diffDays = Math.round(diffMs / 86400000);
    if (diffDays < 0) return `en retard de ${Math.abs(diffDays)} j`;
    if (diffDays === 0) return "aujourd'hui";
    if (diffDays === 1) return "demain";
    if (diffDays < 30) return `dans ${diffDays} j`;
    if (diffDays < 60) return "dans 1 mois";
    return `dans ${Math.round(diffDays / 30)} mois`;
  } catch {
    return dateString;
  }
}

/**
 * Format a date as relative time (e.g., "il y a 5 minutes").
 *
 * @param {string} dateString - ISO date string
 * @returns {string}
 */
export function formatTimeAgo(dateString) {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) return "À l'instant";
    if (diffMinutes < 60) return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? "s" : ""}`;
    if (diffHours < 24) return `Il y a ${diffHours} heure${diffHours > 1 ? "s" : ""}`;
    if (diffDays === 1) return "Hier";
    return `Il y a ${diffDays} jour${diffDays > 1 ? "s" : ""}`;
  } catch {
    return dateString;
  }
}
